// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="FormLayout" set="mytaible-shadcn-studio" verified=tbd

import type { VariantProps } from 'class-variance-authority';
import { AlertTriangle } from 'lucide-react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { cn } from '../../../lib/utils';
import { Button, type buttonVariants } from '../../atoms/button';
import { Separator } from '../../atoms/separator';
import { SectionLayoutFooterSlotContext } from '../../organisms/section-layout-footer-context';

export const FORM_STICKY_EVENT = 'form-layout:sticky-visible';

// ─── Types ────────────────────────────────────────────────────────────────────

export type FormLayoutMaxWidth = 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type FormLayoutActionsPos = 'bottom' | 'top' | 'both' | 'none' | 'sticky';
export type FormLayoutSurface = 'none' | 'muted' | 'card' | 'canvas';
export type FormLayoutSpacing = 'compact' | 'default' | 'relaxed' | 'spacious';
/**
 * Controls whether the form is in edit or view mode.
 * - `edit` (default) — normal interactive form with action buttons
 * - `view`           — read-only display; actions bar is suppressed.
 *   Use `useFormLayoutContext()` in field components to branch between
 *   an editable input and a `<FormDisplayField>`.
 *
 * Named `mode` (not `formState`) to avoid confusion with react-hook-form's
 * `formState` object which is a different concept.
 */
export type FormLayoutMode = 'edit' | 'view';

/**
 * Radius token values shared by fieldRadius, buttonRadius, and cardRadius props.
 * - `none`  → 0px
 * - `sm`    → 0.25rem
 * - `md`    → 0.5rem   (shadcn default)
 * - `lg`    → 0.75rem
 * - `xl`    → 1rem
 * - `full`  → 9999px   (pill / circle)
 */
export type FormLayoutRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

// ─── FormLayoutDefaultsContext ────────────────────────────────────────────────

export interface FormLayoutDefaults {
  spacing?: FormLayoutSpacing;
  actionsPosition?: FormLayoutActionsPos;
  /** Default variant for child FormSection components. */
  sectionVariant?: 'stacked' | 'side-label';
}

const FormLayoutDefaultsContext = React.createContext<FormLayoutDefaults>({});

export function FormLayoutDefaultsProvider({
  children,
  ...defaults
}: FormLayoutDefaults & { children: React.ReactNode }) {
  return (
    <FormLayoutDefaultsContext.Provider value={defaults}>
      {children}
    </FormLayoutDefaultsContext.Provider>
  );
}

export function useFormLayoutDefaults(): FormLayoutDefaults {
  return React.useContext(FormLayoutDefaultsContext);
}

// ─── FormLayoutContext ────────────────────────────────────────────────────────

interface FormLayoutContextValue {
  formState: FormLayoutMode;
  isViewMode: boolean;
}

const FormLayoutContext = React.createContext<FormLayoutContextValue>({
  formState: 'edit',
  isViewMode: false,
});

/**
 * Hook that reads the current form mode from the nearest `<FormLayout>`.
 * Use this in your field components to branch between an editable input
 * and a `<FormDisplayField>` when `formState="view"` is set.
 *
 * @example
 * const { isViewMode } = useFormLayoutContext();
 * return isViewMode
 *   ? <FormDisplayField label="Name" value={values.name} />
 *   : <Input {...field} />;
 */
export function useFormLayoutContext(): FormLayoutContextValue {
  return React.useContext(FormLayoutContext);
}

type BtnVariantProps = VariantProps<typeof buttonVariants>;

export interface FormLayoutActionConfig {
  /** Whether to render this button. Default: true when the config object is provided. */
  show?: boolean;
  /** Button label text. When size="icon" this becomes the aria-label instead. */
  label?: string;
  /** Icon rendered to the left of the label (or as the sole content when size="icon"). */
  icon?: React.ReactNode;
  /** Color intent variant. Default: 'secondary' for cancel, 'primary' for submit. */
  variant?: NonNullable<BtnVariantProps['variant']>;
  /** Visual appearance. Default: 'outline' for cancel, 'solid' for submit. */
  appearance?: NonNullable<BtnVariantProps['appearance']>;
  /**
   * Button size. Use 'icon' to render an icon-only square button —
   * the label is moved to aria-label for accessibility.
   * Default: 'default'.
   */
  size?: NonNullable<BtnVariantProps['size']>;
  /** HTML button type. Default: 'button' for cancel, 'submit' for submit. */
  type?: 'button' | 'submit' | 'reset';
  /** Whether the button is disabled. */
  disabled?: boolean;
  /** Click handler. */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export interface FormLayoutProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Constrains the form column width.
   * - `sm` → max-w-sm  (~384px)  — login modals, compact captures
   * - `md` → max-w-2xl (~672px)  — default single-column settings
   * - `lg` → max-w-3xl (~768px)  — side-label or 3-column forms
   * - `xl` → max-w-5xl (~1024px) — wide dashboard forms
   * - `full` → no max-width constraint
   */
  maxWidth?: FormLayoutMaxWidth;
  /** Renders a <Separator /> between each direct child section. */
  hasSeparators?: boolean;
  /**
   * Where to render the actions bar relative to the sections.
   * - `bottom` (default) — below the last section
   * - `top`              — above the first section
   * - `both`             — mirrored at top and bottom (long forms)
   * - `none`             — no actions bar rendered; parent component owns the buttons
   * - `sticky`           — fixed full-width footer at the bottom of the viewport with
   *                        an "Unsaved changes" label and Discard / Save buttons.
   *                        Pair with `isDirty` to gate visibility:
   *                        omit → always visible · `true` → visible · `false` → hidden.
   *                        Container auto-adds `pb-20` while the footer is showing.
   */
  actionsPosition?: FormLayoutActionsPos;
  /**
   * Structured cancel button config. Provide this object to render the cancel
   * button. All fields optional — unset fields use defaults.
   * Defaults: label="Cancel", variant="secondary", appearance="outline", type="button".
   */
  cancelAction?: FormLayoutActionConfig;
  /**
   * Structured submit button config. Provide this object to render the submit
   * button. All fields optional — unset fields use defaults.
   * Defaults: label="Save changes", variant="primary", appearance="solid", type="submit".
   */
  submitAction?: FormLayoutActionConfig;
  /**
   * Raw escape hatch for fully custom action content.
   * When set, renders instead of cancelAction / submitAction.
   */
  actions?: React.ReactNode;
  /**
   * Controls visibility of the sticky footer. Only evaluated when `actionsPosition="sticky"`.
   * Typically wired to react-hook-form's `formState.isDirty` or equivalent state.
   * Footer only renders when this is `true` — hidden when `false` or omitted.
   */
  isDirty?: boolean;
  /**
   * Label shown on the left side of the sticky footer.
   * Defaults to "Unsaved changes". Only used when `actionsPosition="sticky"`.
   */
  dirtyLabel?: string;
  /**
   * Overrides --field-radius for inputs, selects, textareas, and other field
   * primitives inside the layout. Does NOT affect buttons or cards.
   * When omitted the root --radius token is inherited unchanged.
   */
  fieldRadius?: FormLayoutRadius;
  /**
   * Overrides --button-radius for all action buttons inside the layout.
   * Does NOT affect field inputs or cards.
   * When omitted the root --radius token is inherited unchanged.
   */
  buttonRadius?: FormLayoutRadius;
  /**
   * Overrides --card-radius for Card surfaces inside the layout (used when
   * FormSection asCard=true). Does NOT affect field inputs or buttons.
   * When omitted, Card falls back to --radius.
   */
  cardRadius?: FormLayoutRadius;
  /**
   * Background surface applied to the layout container.
   * - `none`   (default) — transparent, no padding added
   * - `muted`  — bg-muted + p-6
   * - `card`   — bg-card + border + p-6
   * - `canvas` — bg-canvas + p-6
   */
  surface?: FormLayoutSurface;
  /**
   * Vertical spacing between sections.
   * - `compact`  → space-y-4
   * - `default`  → space-y-6 (default)
   * - `relaxed`  → space-y-8
   * - `spacious` → space-y-10
   */
  spacing?: FormLayoutSpacing;
  /**
   * Rounds the layout container. Most useful when `surface` is also set.
   * Uses the same token set as fieldRadius / cardRadius.
   */
  containerRadius?: FormLayoutRadius;
  /**
   * Controls whether the layout is in edit or view mode.
   * - `edit` (default) — interactive form with action buttons
   * - `view`           — read-only display; actions bar is suppressed.
   *   Provides a context consumed via `useFormLayoutContext()`.
   */
  mode?: FormLayoutMode;
  /**
   * Validation errors to surface in an inline alert banner.
   * Pass `form.formState.errors` from react-hook-form directly.
   * Only rendered when at least one error has a message string.
   */
  validationErrors?: Record<string, { message?: string } | undefined>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const maxWidthClass: Record<FormLayoutMaxWidth, string> = {
  sm: 'max-w-sm',
  md: 'max-w-2xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
  full: 'w-full',
};

const radiusCssValue: Record<FormLayoutRadius, string> = {
  none: '0px',
  sm: '0.25rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  full: '9999px',
};

const surfaceClass: Record<FormLayoutSurface, string> = {
  none: '',
  muted: 'bg-muted p-6',
  card: 'bg-card border border-border p-6',
  canvas: 'bg-canvas p-6',
};

const spacingClass: Record<FormLayoutSpacing, string> = {
  compact: 'space-y-3 sm:space-y-4',
  default: 'space-y-4 sm:space-y-6',
  relaxed: 'space-y-6 sm:space-y-8',
  spacious: 'space-y-8 sm:space-y-10',
};

const containerRadiusClass: Record<FormLayoutRadius, string> = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  full: 'rounded-full',
};

// ─── ActionButton ─────────────────────────────────────────────────────────────

export function ActionButton({
  config,
  defaultLabel,
  defaultVariant,
  defaultAppearance,
  defaultType,
}: {
  config: FormLayoutActionConfig;
  defaultLabel: string;
  defaultVariant: NonNullable<BtnVariantProps['variant']>;
  defaultAppearance: NonNullable<BtnVariantProps['appearance']>;
  defaultType: 'button' | 'submit' | 'reset';
}) {
  if (config.show === false) return null;
  const isIconOnly = config.size === 'icon';
  const label = config.label ?? defaultLabel;
  return (
    <Button
      type={config.type ?? defaultType}
      variant={config.variant ?? defaultVariant}
      appearance={config.appearance ?? defaultAppearance}
      size={config.size ?? 'default'}
      disabled={config.disabled}
      onClick={config.onClick}
      aria-label={isIconOnly ? label : undefined}
    >
      {config.icon}
      {!isIconOnly && label}
    </Button>
  );
}

// ─── ActionsBar ───────────────────────────────────────────────────────────────

function ActionsBar({
  actions,
  cancelAction,
  submitAction,
  edge,
}: {
  actions?: React.ReactNode;
  cancelAction?: FormLayoutActionConfig;
  submitAction?: FormLayoutActionConfig;
  edge: 'top' | 'bottom';
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-3',
        edge === 'top' &&
          'sticky top-0 z-10 -mx-8 border-b border-border bg-background/80 px-8 py-4 backdrop-blur-sm',
        edge === 'bottom' && 'py-2',
      )}
    >
      {actions ?? (
        <>
          {cancelAction && (
            <ActionButton
              config={cancelAction}
              defaultLabel="Cancel"
              defaultVariant="secondary"
              defaultAppearance="outline"
              defaultType="button"
            />
          )}
          {submitAction && (
            <ActionButton
              config={submitAction}
              defaultLabel="Save changes"
              defaultVariant="primary"
              defaultAppearance="solid"
              defaultType="submit"
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── ValidationErrorBanner ────────────────────────────────────────────────────

function fieldLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^(.)/, (s) => s.toUpperCase())
    .trim();
}

function ValidationErrorBanner({
  errors,
}: {
  errors: Record<string, { message?: string } | undefined>;
}) {
  const messages = Object.entries(errors).flatMap(([field, v]) =>
    v?.message ? [{ field, message: v.message }] : [],
  );
  if (messages.length === 0) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
      <div>
        <p className="mb-1.5 font-medium leading-none text-destructive">
          Fix the following errors to save:
        </p>
        <ul className="space-y-0.5 text-destructive/90">
          {messages.map(({ field, message }) => (
            <li key={field}>
              <span className="font-medium">{fieldLabel(field)}</span>: {message}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── StickyFooter ─────────────────────────────────────────────────────────────

function StickyFooter({
  actions,
  cancelAction,
  submitAction,
  isDirty,
  dirtyLabel,
}: {
  actions?: React.ReactNode;
  cancelAction?: FormLayoutActionConfig;
  submitAction?: FormLayoutActionConfig;
  isDirty?: boolean;
  dirtyLabel?: string;
}) {
  const slot = React.useContext(SectionLayoutFooterSlotContext);

  if (!isDirty) return null;

  const cancelVisible = cancelAction && cancelAction.show !== false;
  const submitVisible = submitAction && submitAction.show !== false;

  // Mobile: stacked full-width buttons, primary first — matches iOS/Android patterns.
  // Desktop: horizontal status bar — label left, buttons right.
  const inner = (
    <>
      <div className="flex flex-col gap-2 px-4 pt-3 pb-[max(0.875rem,env(safe-area-inset-bottom))] sm:hidden">
        {actions ?? (
          <>
            {submitVisible && (
              <Button
                type={submitAction?.type ?? 'submit'}
                variant={submitAction?.variant ?? 'primary'}
                appearance={submitAction?.appearance ?? 'solid'}
                size="lg"
                disabled={submitAction?.disabled}
                onClick={submitAction?.onClick}
                className="w-full"
              >
                {submitAction?.icon}
                {submitAction?.label ?? 'Save changes'}
              </Button>
            )}
            {cancelVisible && (
              <Button
                type={cancelAction?.type ?? 'button'}
                variant={cancelAction?.variant ?? 'secondary'}
                appearance={cancelAction?.appearance ?? 'ghost'}
                size="default"
                disabled={cancelAction?.disabled}
                onClick={cancelAction?.onClick}
                className="w-full"
              >
                {cancelAction?.icon}
                {cancelAction?.label ?? 'Discard'}
              </Button>
            )}
          </>
        )}
      </div>

      <div className="hidden sm:flex items-center justify-between gap-4 px-6 py-3.5">
        <p className="text-sm text-muted-foreground">{dirtyLabel ?? 'Unsaved changes'}</p>
        <div className="flex items-center gap-2">
          {actions ?? (
            <>
              {cancelAction && (
                <ActionButton
                  config={cancelAction}
                  defaultLabel="Discard"
                  defaultVariant="secondary"
                  defaultAppearance="ghost"
                  defaultType="button"
                />
              )}
              {submitAction && (
                <ActionButton
                  config={submitAction}
                  defaultLabel="Save changes"
                  defaultVariant="primary"
                  defaultAppearance="solid"
                  defaultType="submit"
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );

  if (slot) {
    return ReactDOM.createPortal(
      <div className="border-t border-border bg-background/95 shadow-md backdrop-blur-sm">
        {inner}
      </div>,
      slot,
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 md:left-[var(--shell-sidebar-w,0px)] z-50 border-t border-border bg-background/95 shadow-md backdrop-blur-sm">
      {inner}
    </div>
  );
}

// ─── FormLayout ───────────────────────────────────────────────────────────────

const FormLayout = React.forwardRef<HTMLElement, FormLayoutProps>(
  (
    {
      maxWidth = 'md',
      hasSeparators = false,
      actionsPosition: actionsPositionProp,
      cancelAction,
      submitAction,
      actions,
      isDirty,
      dirtyLabel,
      fieldRadius,
      buttonRadius,
      cardRadius,
      surface = 'none',
      spacing: spacingProp,
      containerRadius,
      mode = 'edit',
      validationErrors,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const layoutDefaults = useFormLayoutDefaults();
    const spacing = spacingProp ?? layoutDefaults.spacing ?? 'default';
    const actionsPosition = actionsPositionProp ?? layoutDefaults.actionsPosition ?? 'bottom';
    const isViewMode = mode === 'view';

    const sections = React.Children.toArray(children).filter(Boolean);

    const body = hasSeparators
      ? sections.flatMap((child, i) =>
          i < sections.length - 1
            ? [child, <Separator key={`sep-${i}`} className="my-2" />]
            : [child],
        )
      : sections;

    const radiusStyle = {
      ...(fieldRadius ? { '--field-radius': radiusCssValue[fieldRadius] } : {}),
      ...(buttonRadius ? { '--button-radius': radiusCssValue[buttonRadius] } : {}),
      ...(cardRadius ? { '--card-radius': radiusCssValue[cardRadius] } : {}),
    } as React.CSSProperties;

    const hasStructuredActions = cancelAction !== undefined || submitAction !== undefined;
    const cancelVisible = cancelAction && cancelAction.show !== false;
    const submitVisible = submitAction && submitAction.show !== false;
    const hasActions =
      actions !== undefined || (hasStructuredActions && (cancelVisible || submitVisible));
    const showActionsBar = !isViewMode && hasActions;

    const inlineBarProps = { actions, cancelAction, submitAction };

    const showTop = showActionsBar && (actionsPosition === 'top' || actionsPosition === 'both');
    const showBottom =
      showActionsBar && (actionsPosition === 'bottom' || actionsPosition === 'both');
    const showSticky = showActionsBar && actionsPosition === 'sticky';
    const stickyVisible = showSticky && !!isDirty;

    React.useEffect(() => {
      window.dispatchEvent(new CustomEvent(FORM_STICKY_EVENT, { detail: stickyVisible }));
    }, [stickyVisible]);

    React.useEffect(() => {
      return () => {
        window.dispatchEvent(new CustomEvent(FORM_STICKY_EVENT, { detail: false }));
      };
    }, []);

    return (
      <FormLayoutContext.Provider value={{ formState: mode, isViewMode }}>
        <section
          ref={ref as React.Ref<HTMLElement>}
          className={cn(
            'mx-auto w-full',
            spacingClass[spacing],
            maxWidthClass[maxWidth],
            surfaceClass[surface],
            containerRadius && containerRadiusClass[containerRadius],
            stickyVisible && 'pb-20',
            className,
          )}
          style={{ ...radiusStyle, ...style }}
          {...props}
        >
          {showTop && <ActionsBar {...inlineBarProps} edge="top" />}
          {validationErrors && <ValidationErrorBanner errors={validationErrors} />}
          {body}
          {showBottom && <ActionsBar {...inlineBarProps} edge="bottom" />}
        </section>
        {showSticky && (
          <StickyFooter {...inlineBarProps} isDirty={isDirty} dirtyLabel={dirtyLabel} />
        )}
      </FormLayoutContext.Provider>
    );
  },
);
FormLayout.displayName = 'FormLayout';

export { FormLayout };
